/** Basemap tile sources for the web app (decided 2026-09-09; see the memo
 *  "Atlas Basemap Sourcing").
 *
 *  Online, the viewer draws Esri ArcGIS Location Platform tiles: an
 *  OpenStreetMap-look street map, World Imagery, and a labels-only overlay
 *  for the satellite view. All three carry the API key, which the app asks
 *  the Worker for at sign-in (GET /api/config, 2026-09-11) rather than
 *  baking into the bundle — a paused Atlas hands out no key, so a fresh load
 *  draws no billable tiles. Esri's licence does not allow storing its tiles
 *  outside its own apps, so the offline cache and offline playback use the NSW
 *  Spatial Services layers instead — Creative Commons Attribution, no key,
 *  and sharper than Esri inside NSW. They are also the shared viewer's
 *  defaults (so the HDD bundle's viewer uses them too); the copy below is
 *  kept identical by basemaps.test.ts rather than imported, because many
 *  web tests mock "@viewer" wholesale and a config module must not depend
 *  on it. The downloader and the cache fingerprinter read this copy. */

import type { TileLayerSet } from "@viewer";

const NSW_SIX = "https://maps.six.nsw.gov.au/arcgis/rest/services/public/";
const NSW_ATTRIBUTION = "© State of New South Wales (Spatial Services)";
/** Mirror of the shared viewer's DEFAULT_TILE_LAYERS (asserted equal in tests). */
const NSW_TILE_LAYERS: TileLayerSet = {
  street: {
    url: `${NSW_SIX}NSW_Base_Map/MapServer/tile/{z}/{y}/{x}`,
    attribution: NSW_ATTRIBUTION,
    maxNativeZoom: 18,
    maxZoom: 22,
  },
  satellite: {
    url: `${NSW_SIX}NSW_Imagery/MapServer/tile/{z}/{y}/{x}`,
    attribution: NSW_ATTRIBUTION,
    maxNativeZoom: 18,
    maxZoom: 22,
  },
};

const STATIC_TILES =
  "https://static-map-tiles-api.arcgis.com/arcgis/rest/services/" +
  "static-basemap-tiles-service/v1/";
const WORLD_IMAGERY =
  "https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/";
const POWERED_BY = "Powered by Esri";

/** Esri ArcGIS Location Platform layers, keyed. */
export function esriTileLayers(apiKey: string): TileLayerSet {
  const token = `?token=${encodeURIComponent(apiKey)}`;
  // Static basemap tiles are 512 px; Leaflet then asks for level z-1, and
  // minNativeZoom 1 keeps map zoom 0 from asking for level -1 (Leaflet
  // clamps the tile zoom before adding the offset).
  const static512 = {
    tileSize: 512, zoomOffset: -1, minNativeZoom: 1, maxNativeZoom: 22, maxZoom: 22,
  };
  return {
    street: {
      url: `${STATIC_TILES}open/osm-style/static/tile/{z}/{y}/{x}${token}`,
      attribution: `${POWERED_BY} | © OpenStreetMap contributors`,
      ...static512,
    },
    satellite: {
      url: `${WORLD_IMAGERY}{z}/{y}/{x}${token}`,
      attribution: `${POWERED_BY} | © Esri, Maxar, Earthstar Geographics`,
      // Capped at 18: World Imagery answers 200 with a "Map data not yet
      // available" placeholder above its regional native zoom, which Leaflet
      // would draw as a tessellated grid. Upscaling is blurry but coherent.
      maxNativeZoom: 18,
      maxZoom: 22,
      labels: {
        url: `${STATIC_TILES}arcgis/imagery/labels/static/tile/{z}/{y}/{x}${token}`,
        ...static512,
      },
    },
  };
}

/** NSW Spatial Services layers: base map + imagery, no key, no labels. */
export function nswTileLayers(): TileLayerSet {
  return NSW_TILE_LAYERS;
}

export const NSW_TILE_HOST = new URL(NSW_TILE_LAYERS.street.url).hostname;
/** Stamped on every offline flight record; a record without it (or with
 *  another value) holds tiles from a retired provider and refresh replaces
 *  them. Bump it if the offline layers ever change provider again. */
export const OFFLINE_BASEMAP_PROVIDER = "nsw";
/** Basemap id → NSW template, for the offline downloader and fingerprinter. */
export const NSW_TILE_TEMPLATES: Record<"map" | "satellite", string> = {
  map: NSW_TILE_LAYERS.street.url,
  satellite: NSW_TILE_LAYERS.satellite.url,
};

export interface SelectTileLayersInput {
  online: boolean;
  /** The viewer page resolved the flight to its offline copy. */
  playingFromCache: boolean;
  esriKey: string;
}

let warnedNoKey = false;

/** Esri when online and playing from the network; NSW otherwise, and also
 *  when no key is configured (a dev build without one still gets a map). */
export function selectTileLayers(input: SelectTileLayersInput): TileLayerSet {
  if (!input.online || input.playingFromCache) return nswTileLayers();
  if (input.esriKey === "") {
    if (!warnedNoKey) {
      warnedNoKey = true;
      console.warn(
        "No Esri API key from /api/config: drawing the NSW Spatial Services " +
          "basemaps instead of Esri",
      );
    }
    return nswTileLayers();
  }
  return esriTileLayers(input.esriKey);
}

/** 1x1 transparent PNG. Drawn in place of a labels overlay when the tile guard
 *  degrades a map to the NSW layers, which publish no labels — the layer stays
 *  on the map without asking Esri for another tile. Kept identical to the
 *  shared viewer's own copy (viewer.js TRANSPARENT_TILE). */
export const TRANSPARENT_TILE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwC" +
  "AAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

/** True for the keyed Esri set (the only one whose tiles cost money). */
export function usesEsri(set: TileLayerSet): boolean {
  return set.satellite.url.includes("arcgis.com");
}
